import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { CheckCircle2, Circle, RotateCcw } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useExpediente,
  useCandidate,
  usePrechecklistTemplate,
  usePrechecklistForExpediente,
  useEffectiveUser,
  useUsersByRole,
} from "@/store/hooks";

export default function Prechecklist() {
  const params = useParams<{ expedienteId: string }>();
  const expedienteId = params.expedienteId ?? "";
  const [, setLocation] = useLocation();

  const expediente = useExpediente(expedienteId);
  const candidate = useCandidate(expediente?.candidatoId);
  const template = usePrechecklistTemplate(expediente?.estandar);
  const state = usePrechecklistForExpediente(expedienteId);
  const me = useEffectiveUser();
  const evaluators = useUsersByRole("evaluador");
  const evalId = (me?.roles.includes("evaluador") ? me.id : evaluators[0]?.id) ?? "evaluador-anon";

  const toggle = useDemoStore((s) => s.togglePrechecklistItem);
  const reset = useDemoStore((s) => s.resetPrechecklist);
  const complete = useDemoStore((s) => s.markPrechecklistComplete);
  const logEvent = useDemoStore((s) => s.logEvent);

  const [, force] = useState(0);
  useEffect(() => { force((n) => n + 1); }, [state?.completedAt]);

  if (!expediente) {
    return (
      <AppLayout breadcrumb={["Evaluador", "Expediente", "Lista preoperativa"]}>
        <Card><div style={{ padding: 20, textAlign: "center" }}>Expediente no encontrado.</div></Card>
      </AppLayout>
    );
  }

  const checkedMap = new Map((state?.entries ?? []).map((e) => [e.itemId, e.checked]));
  const completedCount = template.filter((it) => checkedMap.get(it.id)).length;
  const criticalMissing = template.filter((it) => it.critical && !checkedMap.get(it.id)).length;
  const ready = completedCount === template.length && template.length > 0;
  const alreadyDone = !!state?.completedAt;

  return (
    <AppLayout breadcrumb={["Evaluador", `Expediente ${expedienteId}`, "Lista preoperativa"]}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>Lista de cotejo preoperativa</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>
            {candidate?.nombre} · {expediente.estandar} · {template.length} ítems
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {alreadyDone && <Badge tone="green">Aplicada · {state?.completedAt}</Badge>}
          <div style={{ fontSize: 13, color: "var(--cc-gray-700)", fontWeight: 600 }} data-testid="text-progress">
            {completedCount} / {template.length} verificados
          </div>
        </div>
      </div>

      {template.length === 0 ? (
        <Card>
          <SectionTitle>Sin lista preoperativa configurada</SectionTitle>
          <div style={{ fontSize: 13, color: "var(--cc-gray-600)" }}>
            El estándar {expediente.estandar} no tiene una lista de cotejo preoperativa configurada. Pide al administrador que la cree.
          </div>
        </Card>
      ) : (
        <Card padding={0}>
          {template.map((it, idx) => {
            const isChecked = !!checkedMap.get(it.id);
            return (
              <button
                key={it.id}
                onClick={() => toggle(expedienteId, it.id, !isChecked)}
                data-testid={`check-${it.id}`}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12,
                  padding: "14px 18px", textAlign: "left",
                  border: "none", borderTop: idx === 0 ? "none" : "1px solid var(--cc-gray-100)",
                  background: isChecked ? "var(--cc-green-bg)" : "white",
                  cursor: "pointer",
                }}
              >
                {isChecked ? <CheckCircle2 size={20} color="var(--cc-green)" /> : <Circle size={20} color="var(--cc-gray-400)" />}
                <div style={{ flex: 1, fontSize: 13, color: "var(--cc-gray-800)", fontWeight: isChecked ? 600 : 500 }}>
                  {it.label}
                </div>
                {it.critical && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--cc-red)", padding: "2px 6px", background: "var(--cc-red-bg)", borderRadius: 4 }}>CRÍTICO</span>}
              </button>
            );
          })}
        </Card>
      )}

      {criticalMissing > 0 && (
        <div style={{ marginTop: 12, padding: "10px 14px", background: "var(--cc-red-bg)", color: "var(--cc-red)", borderRadius: 8, fontSize: 12, fontWeight: 600 }}>
          {criticalMissing} ítem(s) crítico(s) sin verificar.
        </div>
      )}

      <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", gap: 8 }}>
        <PrimaryButton variant="ghost" onClick={() => { reset(expedienteId); }} testId="button-reset">
          <RotateCcw size={13} /> Reiniciar lista
        </PrimaryButton>
        <div style={{ display: "flex", gap: 8 }}>
          <PrimaryButton variant="ghost" onClick={() => setLocation("/evaluador")} testId="button-back">← Volver a la agenda</PrimaryButton>
          <PrimaryButton
            disabled={template.length === 0}
            onClick={() => {
              complete(expedienteId, evalId);
              logEvent({ actor: evalId, rol: "evaluador", accion: ready ? "completó lista preoperativa" : "registró lista preoperativa parcial", entidad: "expediente", entidadId: expedienteId });
              setLocation(`/evaluador/evaluacion/${expedienteId}`);
            }}
            testId="button-go-rubric"
          >
            {ready ? "Habilitar circuito →" : `Continuar igual (${template.length - completedCount} pendientes)`}
          </PrimaryButton>
        </div>
      </div>
    </AppLayout>
  );
}
